import React from 'react'

import {
  Box,
  Card,
  Text,
  Title,
  TogglerCheckbox,
  WizardStepRenderParams,
} from '@factorialco/deprecated-design-system'
import { F0Alert } from '@factorialco/f0-react'
import { RichTextEditor, RichTextEditorHandle } from '@factorialco/f0-react/dist/experimental'
import TextEditor from 'modules/core/components/TextEditor'
import TextField from 'modules/core/components/TextField'
import { useMemo } from 'modules/core/hooks/useMemo'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import i18n from 'modules/core/lib/i18n'

import { FormWizardData } from 'modules/trainings/components/Revamp/NewFormWizard/types'

export const BasicInfoStep = ({ data, onDataChanged }: WizardStepRenderParams<FormWizardData>) => {
  const { editorConfig, canUseRichTextEditor, loading: richTextEditorLoading } = useRichTextEditor()
  const descriptionRichTextEditorRef = React.useRef<RichTextEditorHandle>(null)

  const placeholder = useMemo(() => {
    switch (data.trainingFormType) {
      case 'effectiveness':
        return i18n.t('trainings.surveys.create.effectiveness.title')
      case 'satisfaction':
      default:
        return i18n.t('trainings.surveys.create.satisfaction.title')
    }
  }, [data.trainingFormType])

  const descriptionPlaceholder = (() => {
    switch (data.trainingFormType) {
      case 'effectiveness':
        return i18n.t('trainings.surveys.create.effectiveness.description')
      case 'satisfaction':
      default:
        return i18n.t('trainings.surveys.create.satisfaction.description')
      case 'knowledge':
        return i18n.t('trainings.surveys.create.knowledge.description')
    }
  })()

  return (
    <Box gap="s16" width="s600">
      <Title title={i18n.t('trainings.surveys.create.basic_information')} variant="500" />
      <TextField
        value={data.title}
        label={i18n.t('trainings.surveys.create.survey_name')}
        placeholder={placeholder}
        onChange={(value) => onDataChanged({ ...data, title: value })}
        mandatory
      />
      <Box gap="s4">
        <Text.Normal size="200" weight="medium" color="grey1000">
          {i18n.t('trainings.surveys.create.description')}
        </Text.Normal>
        {richTextEditorLoading ? (
          <RichTextEditor.Skeleton />
        ) : canUseRichTextEditor ? (
          <RichTextEditor
            ref={descriptionRichTextEditorRef}
            {...editorConfig}
            onChange={(value) => onDataChanged({ ...data, description: value?.value ?? '' })}
            placeholder={descriptionPlaceholder}
            initialEditorState={{ content: data.description || '' }}
            title={i18n.t('trainings.surveys.create.description')}
            height="sm"
          />
        ) : (
          <TextEditor
            readonly={false}
            placeholder={descriptionPlaceholder}
            height="s200"
            initialEditorState={{ data: data.description }}
            onChange={(value) =>
              onDataChanged({ ...data, description: String(value ?? data.description) })
            }
          />
        )}
      </Box>
      {data.trainingFormType === 'effectiveness' && (
        <F0Alert
          variant="info"
          title={i18n.t('trainings.surveys.create.effectiveness.warning')}
          description=""
        />
      )}

      {data.trainingFormType === 'satisfaction' && (
        <Box gap="s8" position="sticky">
          <Card>
            <Card.Title
              size="small"
              action={
                <TogglerCheckbox
                  onChange={(value) => {
                    onDataChanged({ ...data, anonymous: value })
                  }}
                  value={data.anonymous}
                />
              }
            >
              {i18n.t('trainings.surveys.create.anonymous.title')}
            </Card.Title>
            <Card.Description>
              {i18n.t('trainings.surveys.create.anonymous.description')}
            </Card.Description>
          </Card>
        </Box>
      )}
    </Box>
  )
}
